import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ElementType, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

const typographyVariants = cva('', {
    variants: {
        variant: {
            display: 'text-display',
            'heading-1': 'text-heading-1',
            'heading-2': 'text-heading-2',
            'heading-3': 'text-heading-3',
            'card-title': 'text-card-title',
            'card-heading': 'text-card-heading',
            body: 'text-body',
            'body-emphasis': 'text-body-emphasis',
            caption: 'text-caption',
            'subtle-heading': 'text-subtle-heading',
            'table-group': 'text-table-group',
            'table-header-group': 'text-table-header-group',
        },
    },
});

export type TypographyVariant = NonNullable<VariantProps<typeof typographyVariants>['variant']>;

const roleElement: Record<TypographyVariant, ElementType> = {
    display: 'p',
    'heading-1': 'h1',
    'heading-2': 'h2',
    'heading-3': 'h3',
    'card-title': 'h3',
    'card-heading': 'h3',
    body: 'p',
    'body-emphasis': 'p',
    caption: 'p',
    'subtle-heading': 'p',
    'table-group': 'p',
    'table-header-group': 'p',
};

export type TypographyProps = {
    variant?: TypographyVariant;
} & Omit<ComponentPropsWithRef<'p'>, 'color'>;

/**
 * Renders text in a typography role, choosing the semantic element from the
 * role itself — headings become `h1`/`h2`/`h3`, everything else `p`. There is
 * deliberately no `as` prop: the element and the role stay in lock-step so
 * semantics can't drift from the visual style.
 *
 * @example
 * <Typography variant="heading-1">Team</Typography>            // <h1>
 * <Typography variant="body" className="text-content-weaker">Card subtitle</Typography>  // <p>
 * <Typography variant="body" className="text-danger">€144.201</Typography>
 */
export function Typography({ variant = 'body', className, ...props }: TypographyProps): ReactElement {
    const Tag = roleElement[variant];
    return <Tag className={cn(typographyVariants({ variant }), className)} {...props} />;
}
